import React, { useState } from 'react';
import Title from 'antd/lib/typography/Title';
import Text from 'antd/lib/typography/Text';
import { Layout, Row, Card, Alert, Input, Col } from 'antd';
import { useHistory, useParams } from 'react-router-dom';
import { api } from '../../utils/axiosConfig';
import ResourceCreateCommandRow from '../common/resourceCreateCommandRow';
import stripDisallowedIdCharacters from '../../utils/stripDisallowedIdCharacters';
import { EditorState } from '../common/editorEnum';
import { ActiveChannels, Channels } from '../common/channelsEnum';
import DefaultTemplates from './DefaultTemplates';
import { AxiosError } from 'axios';

const { Content } = Layout;

type Props = {
  // This is for the tests to get an onsubmit callback
  onSubmit?: (response: unknown) => void;
};

const NewNotification = ({ onSubmit }: Props): JSX.Element => {
  const { accountId } = useParams<{ accountId: string }>();
  const [titleState, changeTitleState] = useState('');
  const [idState, changeIdState] = useState('');
  const [accurateIdState, changeAccurateIdState] = useState(false);
  const [redirectSubmit] = useState(false);
  const [submittingForm, changeSubmittingForm] = useState(false);
  const history = useHistory();

  const handleSubmit = async (): Promise<void> => {
    changeSubmittingForm(true);
    const parm = {
      channels: [ActiveChannels.EMAIL, ActiveChannels.INAPP_WEB],
      notificationId: idState,
      title: titleState
    };

    try {
      await api('POST', `${accountId}/notifications`, parm);
    } catch (e) {
      const error = e as AxiosError;
      if (
        error.response?.data.message &&
        error.response?.data.message.includes('duplicate notificationId')
      ) {
        changeAccurateIdState(true);
        changeSubmittingForm(false);
        return;
      }
      throw e;
    }

    await Promise.all(
      Object.values(Channels).map(async (channel) => {
        return api(
          'POST',
          `/${accountId}/notifications/${idState}/templates/${channel}`,
          DefaultTemplates[channel]
        ).catch((e) => {
          const error = e as AxiosError;
          if (
            error.response?.data.message &&
            error.response?.data.message.includes('duplicate templateId')
          ) {
            // do nothing
            return;
          }
          throw e;
        });
      })
    );
    history.push(
      `/${accountId}/notifications/${idState}/notificationConfig/overview`
    );
  };

  return (
    <Content style={{ padding: '24px' }}>
      <Row>
        <Col>
          <Card>
            {accurateIdState && (
              <Alert
                message="Error"
                description="There is already a notification with the same ID. Please change the ID."
                type="error"
                showIcon
                style={{ margin: '20px' }}
              />
            )}
            <Row justify="space-between">
              <Col>
                <Row>
                  <Title type="secondary" level={5}>
                    Title:
                  </Title>
                </Row>
                <Row>
                  <Input
                    data-cy="Notificatio_Name"
                    data-testid="notification-name"
                    style={{ width: 400 }}
                    defaultValue={titleState}
                    placeholder="e.g. New Comment, Threshold Alert, Review Required"
                    onChange={(
                      e: React.ChangeEvent<HTMLInputElement>
                    ): void => {
                      changeTitleState(e.target.value);
                      changeIdState(
                        stripDisallowedIdCharacters(e.target.value)
                      );
                    }}
                  />
                </Row>
                <Row>
                  <Text type="secondary">
                    Human-readable name for the notification
                  </Text>
                </Row>

                <Row style={{ marginTop: '24px' }}>
                  <Title type="secondary" level={5}>
                    Notification ID:
                  </Title>
                </Row>
                <Row>
                  <Input
                    style={
                      accurateIdState
                        ? { width: 400, border: 'thick double red' }
                        : { width: 400 }
                    }
                    value={idState}
                    onChange={(
                      e: React.ChangeEvent<HTMLInputElement>
                    ): void => {
                      changeIdState(
                        stripDisallowedIdCharacters(e.target.value)
                      );
                    }}
                  />
                </Row>
                <Row>
                  <Text type="secondary">Used in code to make API calls</Text>
                </Row>

                <ResourceCreateCommandRow
                  postRedirectURL={`/${accountId}/notifications/${idState}/notificationConfig/overview`}
                  cancelRedirectURL={`/${accountId}/notifications`}
                  handleSubmit={handleSubmit}
                  redirectSubmit={redirectSubmit}
                  submittingForm={submittingForm}
                  editorState={EditorState.NEW}
                  disableCreateButton={titleState === ''}
                />
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
    </Content>
  );
};

export default NewNotification;
